import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { navItems } from '@/constants';
// import Button from '@/components/ui/Button';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (item: any) => {
    if (item.isRoute) {
      return; // Let Link component handle route navigation
    }
    
    // For hash links, check if we're on homepage
    if (location.pathname === '/') {
      // On homepage, scroll to section directly
      const hash = item.href.includes('#') ? item.href.substring(item.href.indexOf('#')) : item.href;
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsOpen(false);
    } else {
      // On other pages, navigate to homepage with hash
      window.location.href = item.href;
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    >
      <div 
        className={`container mx-auto px-4 md:px-8 py-6 fixed top-3 left-0 right-0 md:left-4 md:right-4 transition-all duration-300 ${
          isScrolled
            ? 'bg-black/20 backdrop-blur-lg shadow-lg'
            : 'bg-black/20 backdrop-blur-lg'
        }`}
        style={{
          borderTopColor: '#04AD2F',
          borderLeftColor: '#04AD2F',
          borderRightColor: '#EF9C00',
          borderBottomColor: '#EF9C00',
          borderWidth: '0.5px',
          borderRadius: isOpen ? '24px' : '50px',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <nav className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/images/logo.svg" alt="Logo" className="h-8" />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center space-x-8 mx-auto">
            {navItems.map((item) => (
              item.isRoute ? (
                <Link
                  key={item.title}
                  to={item.href}
                  className="text-white hover:text-primary-600 transition-colors font-medium"
                >
                  {item.title}
                </Link>
              ) : (
                <button
                  key={item.title}
                  onClick={() => handleNavClick(item)}
                  className="text-white hover:text-primary-600 transition-colors font-medium"
                >
                  {item.title}
                </button>
              )
            ))}
          </div>

          {/* Join Waitlist Button */}
          <div className="hidden lg:flex items-center">
            <button 
              onClick={() => window.location.href = '/waitlist'}
              className="group bg-white text-foodsply-green px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center text-sm"
            >
              Join Waitlist
              <img src="/images/arrow-right-1.svg" alt="" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 py-4 border-t border-white/20"
            >
              <div className="flex flex-col space-y-4 px-2">
                {navItems.map((item) => (
                  item.isRoute ? (
                    <Link
                      key={item.title}
                      to={item.href}
                      className="text-white hover:text-gray-200 transition-colors font-medium text-left"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <button
                      key={item.title}
                      onClick={() => handleNavClick(item)}
                      className="text-white hover:text-gray-200 transition-colors font-medium text-left"
                    >
                      {item.title}
                    </button>
                  )
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;